import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BottomNav } from "@/components/BottomNav";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import TransactionDetail from "@/pages/TransactionDetail";
import AddTransaction from "@/pages/AddTransaction";
import Reports from "@/pages/Reports";
import More from "@/pages/More";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import CategoriesTags from "@/pages/CategoriesTags";
import Businesses from "@/pages/Businesses";
import IncomeSources from "@/pages/IncomeSources";
import Members from "@/pages/Members";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
      } catch (e) {
        console.error("session exchange failed", e);
      }
      window.history.replaceState(null, "", window.location.pathname);
      await refresh();
      navigate("/", { replace: true });
    })();
  }, []); // eslint-disable-line

  return <Spinner />;
};

const Protected = ({ children, shell = true }) => {
  const { user, household, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!household) return <Navigate to="/onboarding" replace />;
  return (
    <div className="max-w-md mx-auto min-h-screen bg-background relative sm:border-x sm:border-border">
      <div className={shell ? "pb-24" : ""}>{children}</div>
      {shell && <BottomNav />}
    </div>
  );
};

const OnboardingGate = () => {
  const { user, household, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (household) return <Navigate to="/" replace />;
  return <Onboarding />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
      <Route path="/transactions/:id" element={<Protected shell={false}><TransactionDetail /></Protected>} />
      <Route path="/add" element={<Protected shell={false}><AddTransaction /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/more" element={<Protected><More /></Protected>} />
      <Route path="/more/accounts" element={<Protected><Accounts /></Protected>} />
      <Route path="/more/accounts/:id" element={<Protected shell={false}><AccountDetail /></Protected>} />
      <Route path="/more/categories" element={<Protected><CategoriesTags /></Protected>} />
      <Route path="/more/businesses" element={<Protected><Businesses /></Protected>} />
      <Route path="/more/income-sources" element={<Protected><IncomeSources /></Protected>} />
      <Route path="/more/members" element={<Protected><Members /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
